/*
  Next day delivery countdown for the Order options card.

  Stays hidden unless the promise actually holds: Monday to Thursday only (ordering on a Friday
  would land on a weekend), after opening, before the cutoff, and never when today or tomorrow is
  a bank holiday. Gating is done here rather than in Liquid because the page HTML is cached.

  Times are read in Europe/London, so GMT and BST need no special handling.
*/

const OPEN_HOUR = 6;
const CUTOFF_HOUR = 14;
const DAY_MS = 86400000;

const UK_CLOCK_FORMAT = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/London',
  hourCycle: 'h23',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
});

/* The current UK wall clock as plain numbers, which keeps the caller free of timezone maths. */
const readUkClock = () => {
  const clock = {};

  for (const part of UK_CLOCK_FORMAT.formatToParts(new Date())) {
    clock[part.type] = Number(part.value);
  }

  return clock;
};

const toDateKey = (utcMs) => new Date(utcMs).toISOString().slice(0, 10);

const formatRemaining = (ms) => {
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor(totalSeconds / 60) % 60;
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return `${hours}h ${minutes}m ${seconds}s`;
  }

  return `${minutes}m ${seconds}s`;
};

class OrderOptionsCountdown extends HTMLElement {
  #timeElement = null;
  #timer = null;
  #deadline = 0;
  #rendered = '';

  connectedCallback() {
    this.#timeElement = this.querySelector('.order-options__countdown-time');

    const remaining = this.#resolveRemaining();

    if (!this.#timeElement || remaining <= 0) {
      return;
    }

    this.#deadline = Date.now() + remaining;
    this.#tick();
    this.hidden = false;
    this.#timer = window.setInterval(() => this.#tick(), 1000);
  }

  disconnectedCallback() {
    window.clearInterval(this.#timer);
  }

  /* Milliseconds left before the cutoff, or 0 when tomorrow cannot be promised. */
  #resolveRemaining() {
    const clock = readUkClock();
    const today = Date.UTC(clock.year, clock.month - 1, clock.day);
    const weekday = new Date(today).getUTCDay();

    if (weekday < 1 || weekday > 4 || clock.hour < OPEN_HOUR) {
      return 0;
    }

    const holidays = (this.dataset.holidays || '').split(/[\s,]+/);

    if (holidays.includes(toDateKey(today)) || holidays.includes(toDateKey(today + DAY_MS))) {
      return 0;
    }

    const elapsed = clock.hour * 3600 + clock.minute * 60 + clock.second;

    return Math.max(0, (CUTOFF_HOUR * 3600 - elapsed) * 1000);
  }

  #tick() {
    const remaining = this.#deadline - Date.now();

    if (remaining <= 0) {
      window.clearInterval(this.#timer);
      this.hidden = true;
      return;
    }

    const next = formatRemaining(remaining);

    /* Only touch the DOM when the visible value changes. */
    if (next !== this.#rendered) {
      this.#rendered = next;
      this.#timeElement.textContent = next;
    }
  }
}

if (!customElements.get('order-options-countdown')) {
  customElements.define('order-options-countdown', OrderOptionsCountdown);
}

/*
  Keeps the {price} installment amount in sync with the selected variant.
  Amounts are pre-formatted in Liquid; this only swaps the string on variant change.
*/
class OrderOptionsPayment extends HTMLElement {
  #form = null;
  #priceElement = null;
  #installments = new Map();
  #onFormChange = null;

  connectedCallback() {
    this.#priceElement = this.querySelector('.order-options__payment-price');
    this.#form = document.getElementById(this.dataset.formId);

    const dataScript = this.querySelector('script[type="application/json"]');

    if (!this.#priceElement || !this.#form || !dataScript) {
      return;
    }

    try {
      const variants = JSON.parse(dataScript.textContent);

      for (const variant of variants) {
        this.#installments.set(String(variant.id), variant.installment);
      }
    } catch {
      return;
    }

    dataScript.remove();
    this.#onFormChange = () => this.#syncPrice();
    this.#form.addEventListener('change', this.#onFormChange);
    this.#syncPrice();
  }

  disconnectedCallback() {
    if (this.#form && this.#onFormChange) {
      this.#form.removeEventListener('change', this.#onFormChange);
    }
  }

  #syncPrice() {
    const variantInput = this.#form.querySelector('input[name="id"]');
    const variantId = variantInput?.value;

    if (!variantId) {
      return;
    }

    const installment = this.#installments.get(String(variantId));

    if (installment) {
      this.#priceElement.textContent = installment;
    }
  }
}

if (!customElements.get('order-options-payment')) {
  customElements.define('order-options-payment', OrderOptionsPayment);
}
